// citaLinks.js — pure deep-link resolution for the Calendar cita detail panel
// (feat-calendar-ux-sep3, Roger 2026-09-03: "clicking the appointment should take
// you to the campaign or to the postventa client so you can see the transcription
// and how the call went").
//
// Given a loaded appointment row, derive the set of navigation targets the panel
// should offer, each as {to, label, disabled, reason}. Kept pure + framework-free
// so the button matrix (with/without ids) is unit-testable without React/router.
//
// Routes reused (already live in App.js):
//   /postventa?vin=<vin>&phone=<phone>        -> postventa client (transcript + WA thread)
//   /campaigns/:id?focusContact=<cid>|focusPhone=<phone>  -> campaign contact (transcript)
//   /whatsapp?phone=<phone>&dept=<dept>       -> WhatsApp conversation
//   /call-history?callSid=<sid>               -> call detail (transcript + recording)
//
// DESIGN: never invent an id. When the id a target needs is absent on the row we
// still surface the button, DISABLED, with a reason the UI renders as a tooltip
// ("sin registro vinculado"), so the operator sees the capability exists and knows
// the backend simply didn't link it — instead of a silently missing button.

import { DEPT_POSTVENTA, DEPT_VENTAS, SOURCE_BPRO } from './calendarTags';

// Normalize a phone to a URL-safe query value: keep digits only, so a '+' can't
// round-trip through the query string as a space (the postventa/campaign focus
// matchers already canonicalize on the last-10 national significant number).
export const phoneParam = (phone) => String(phone || '').replace(/[^\d]/g, '');

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    const s = String(v == null ? '' : v).trim();
    if (s) return s;
  }
  return '';
};

// The VIN a row carries (native rows persist `vin`; B.PRO postventa projections
// carry `vin`/`serie`; fall back to nothing — never guess from a free-text field).
export const citaVin = (appt) => firstNonEmpty(appt && appt.vin, appt && appt.serie);

// The phone the row carries, in raw form (for display) — callers pass it through
// phoneParam() before putting it in a URL.
export const citaPhone = (appt) => firstNonEmpty(
  appt && appt.customer_phone,
  appt && appt.phone,
);

// A call SID (voice) the row carries, for the "Ver llamada" deep link.
export const citaCallSid = (appt) => firstNonEmpty(
  appt && appt.call_sid,
  appt && appt.callSid,
);

// Build the query string from an object, dropping empty values, URL-encoding
// each part. Returns '' when nothing to add (so callers append cleanly).
const qs = (obj) => {
  const parts = Object.entries(obj)
    .filter(([, v]) => String(v == null ? '' : v).trim() !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

// Resolve the ordered list of deep-link actions for a cita row. Each entry:
//   { key, label, to, disabled, reason }
// `to` is a react-router path (null when disabled). `reason` is an i18n KEY the
// panel renders as the disabled tooltip. `label` is also an i18n key.
//
// Ordering: the primary "who is this" jump first (client for postventa, campaign
// for ventas), then the conversation (WhatsApp), then the call.
export const resolveCitaLinks = (appt) => {
  if (!appt) return [];
  const dept = appt._department || appt.department;
  const isPostventa = dept === DEPT_POSTVENTA;
  const isBpro = appt._source === SOURCE_BPRO;
  const phone = citaPhone(appt);
  const phoneQ = phoneParam(phone);
  const vin = citaVin(appt);
  const callSid = citaCallSid(appt);
  const campaignId = firstNonEmpty(appt.campaign_id, appt.campaignId);
  const contactId = firstNonEmpty(appt.contact_id, appt.contactId);

  const links = [];

  // --- Primary "Ver cliente" (postventa) --------------------------------------
  // Postventa clients live at /postventa keyed by VIN; phone lets the page open
  // the WhatsApp thread even when the VIN is missing. Enabled if EITHER id exists.
  if (isPostventa) {
    const enabled = !!(vin || phoneQ);
    links.push({
      key: 'client',
      label: 'calendar.links.viewClient',
      to: enabled ? `/postventa${qs({ vin, phone: phoneQ })}` : null,
      disabled: !enabled,
      reason: enabled ? '' : 'calendar.links.noLink',
    });
  } else {
    // --- Primary "Ver campaña/contacto" (ventas) ------------------------------
    // Campaign contact detail with transcript. Needs the campaign id to know
    // WHICH campaign; the contact is focused by contact_id (preferred) or phone.
    const enabled = !!campaignId && !!(contactId || phoneQ);
    const focus = contactId ? { focusContact: contactId } : { focusPhone: phoneQ };
    links.push({
      key: 'campaign',
      label: 'calendar.links.viewCampaign',
      to: enabled ? `/campaigns/${encodeURIComponent(campaignId)}${qs(focus)}` : null,
      disabled: !enabled,
      reason: enabled ? '' : 'calendar.links.noLink',
    });
  }

  // --- "Ver conversación WhatsApp" -------------------------------------------
  // Available whenever the row carries a phone (a WA thread is keyed by phone +
  // department). B.PRO rows with only an external cita id and no phone stay
  // disabled. Skip entirely for B.PRO manual rows that never had an AI thread?
  // No — we still offer it when a phone exists, since the inbox can surface any
  // thread for that number; disabled+tooltip when there's no phone at all.
  {
    const enabled = !!phoneQ;
    const deptParam = isPostventa ? 'postventa' : 'ventas';
    links.push({
      key: 'whatsapp',
      label: 'calendar.links.viewWhatsapp',
      to: enabled ? `/whatsapp${qs({ phone: phoneQ, dept: deptParam })}` : null,
      disabled: !enabled,
      reason: enabled ? '' : 'calendar.links.noThread',
    });
  }

  // --- "Ver llamada" ----------------------------------------------------------
  // Only when a call SID exists (voice-booked citas). B.PRO/manual/WA rows have
  // none -> disabled with tooltip. Never shown for pure B.PRO rows lacking a SID.
  {
    const enabled = !!callSid && !isBpro;
    links.push({
      key: 'call',
      label: 'calendar.links.viewCall',
      to: enabled ? `/call-history${qs({ callSid })}` : null,
      disabled: !enabled,
      reason: enabled ? '' : 'calendar.links.noCall',
    });
  }

  return links;
};

export default resolveCitaLinks;
